import React, { useState, useEffect } from 'react';
import api from '../../services/apiService';
import './scss/scoresModal.scss';
import logo from '../../images/logo-long.png';
import { format } from 'date-fns';
import { useNotification } from '../../components/NotificationProvider';

const ScoresModal = ({ match, onClose, onUpdate }) => {
  const [goals, setGoals] = useState({});
  const [ownGoals, setOwnGoals] = useState({});
  const [teams, setTeams] = useState({ White: [], Colour: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [attendance, setAttendance] = useState({ yes: [], no: [], awaiting: [] });
  const [isPublishing, setIsPublishing] = useState(false);
  const [oppositionScore, setOppositionScore] = useState(match.score_colour ? parseInt(match.score_colour, 10) : ''); // For academy teams
  const [oppositionOwnGoals, setOppositionOwnGoals] = useState(
    match.opposition_own_goals !== undefined && match.opposition_own_goals !== null
      ? parseInt(match.opposition_own_goals, 10) || 0
      : ''
  );
  
  // Update opposition score when match data changes
  useEffect(() => {
    if (match.score_colour !== undefined && match.score_colour !== null) {
      setOppositionScore(parseInt(match.score_colour, 10) || 0);
    }
  }, [match.score_colour]);
  const { showSuccess, showError } = useNotification();
  
  // Get team names
  const currentTeamName = sessionStorage.getItem('selected_team_name');
  const teamA = match.team_a || (match.is_home ? (match.team_name || currentTeamName) : match.opposition) || 'White';
  const teamB = match.team_b || (match.is_home ? match.opposition : (match.team_name || currentTeamName)) || 'Colour';
  
  // Check if it's academy or squad mode (single team)
  const selectedTeamType = sessionStorage.getItem('selected_team_type');
  const isAcademyOrSquad = ['academy', 'squad'].includes(selectedTeamType) ||
    ['academy', 'squad'].includes(match.team_type);
  const isFriendlyMode = !isAcademyOrSquad;

  // Check if match is finished
  const isMatchFinished = () => {
    if (!match) {
      return false;
    }
    
    if (!match.match_date || !match.match_time) {
      return false;
    }
    
    // Parse match date and time to create a Date object
    const [year, month, day] = match.match_date.split('-');
    const [hours, minutes, seconds] = match.match_time.split(':');
    
    // JavaScript months are 0-indexed (0-11), so subtract 1 from month
    const matchStartTime = new Date(year, month-1, day, hours, minutes, seconds);
    
    // Get duration in minutes (default to 60 if not specified)
    const duration = parseInt(match.duration) || 60;
    
    // Calculate end time by adding duration to start time
    const matchEndTime = new Date(matchStartTime.getTime());
    matchEndTime.setMinutes(matchEndTime.getMinutes() + duration);
    
    return new Date() > matchEndTime;
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // For academy teams, fetch attendance data first to get all "Yes" players
        if (isAcademyOrSquad) {
          const attendanceResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_attendance.php?match_id=${match.match_id}&team_id=${sessionStorage.getItem('selected_team_id')}`);
          if (attendanceResponse.data.success) {
            setAttendance(attendanceResponse.data.data);
            
            // For academy teams, use all "Yes" players instead of position data
            const yesPlayers = attendanceResponse.data.data.yes || [];
            console.log("Academy team - Yes players:", yesPlayers);
            
            const initialGoals = {};
            const initialOwnGoals = {};
            
            const academyTeam = yesPlayers.map(player => {
              initialGoals[player.player_id] = 0;
              initialOwnGoals[player.player_id] = 0;
              
              return {
                player_id: player.player_id,
                first_name: player.first_name,
                last_name: player.last_name,
                team_color: 'white', // Academy teams use single team
                position: 'Player',
                goals: 0,
                own_goals: 0
              };
            });
            
            setTeams({
              White: academyTeam,
              Colour: []
            });
            
            setGoals(initialGoals);
            setOwnGoals(initialOwnGoals);
          }
        } else {
          // For friendly teams, fetch player positions data from match_player_positions.php
          const positionsResponse = await api.get(`${process.env.REACT_APP_API_URL}/match_player_positions.php?match_id=${match.match_id}`);
          if (Array.isArray(positionsResponse.data) && positionsResponse.data.length > 0) {
            console.log("Teams data from match_player_positions.php:", positionsResponse.data);
          
          // Fetch player details to combine with positions
          const playersResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_match_player_details.php?match_id=${match.match_id}`);
          const playerDetailsMap = {};
          
          console.log("Player details response:", playersResponse.data);
          
          if (playersResponse.data.status === 'success' && Array.isArray(playersResponse.data.data)) {
            playersResponse.data.data.forEach(player => {
              // Store by both id and player_id to handle different API formats
              if (player.id) {
                playerDetailsMap[player.id] = player;
              }
              // Some APIs might use player_id instead of id
              if (player.player_id) {
                playerDetailsMap[player.player_id] = player;
              }
              
              // If we have a name property but not first_name/last_name, split it
              if (player.name && (!player.first_name || !player.last_name)) {
                const nameParts = player.name.split(' ');
                if (nameParts.length > 1) {
                  player.first_name = nameParts[0];
                  player.last_name = nameParts.slice(1).join(' ');
                } else {
                  player.first_name = player.name;
                  player.last_name = '';
                }
              }
            });
          }
          
          console.log("Player details map:", playerDetailsMap);
          
          // Log attendance data for debugging
          console.log("Attendance data:", attendance);
          
          // Organize players by team color
          const whiteTeam = [];
          const colourTeam = [];
          
          // Initialize goals and own goals objects
          const initialGoals = {};
          const initialOwnGoals = {};
          
          // Process positions data
          for (const position of positionsResponse.data) {
            const playerId = position.player_id;
            const teamColor = position.team_color;
            
            console.log(`Processing player ID ${playerId} with team color ${teamColor}`);
            
            // Try to get player details
            let playerDetails = null;
            
            // If we have player details, use them
            if (playerDetailsMap[playerId]) {
              console.log(`Found player details for ID ${playerId}:`, playerDetailsMap[playerId]);
              playerDetails = playerDetailsMap[playerId];
            } else {
              console.log(`No player details found for ID ${playerId}, looking in attendance data`);
              // Otherwise use fallback data from attendance
              const attendancePlayer = [...(attendance.yes || []), ...(attendance.no || []), ...(attendance.awaiting || [])]
                .find(p => p.player_id == playerId);
                
              if (attendancePlayer) {
                console.log(`Found player in attendance data:`, attendancePlayer);
                playerDetails = {
                  first_name: attendancePlayer.first_name,
                  last_name: attendancePlayer.last_name
                };
              } else {
                console.log(`No attendance data found for player ID ${playerId}, using fallback`);
                // Last resort fallback
                playerDetails = {
                  first_name: "Player",
                  last_name: `#${playerId}`
                };
              }
            }
            
            // Ensure we have valid first_name and last_name
            if (!playerDetails.first_name) playerDetails.first_name = "Player";
            if (!playerDetails.last_name) playerDetails.last_name = `#${playerId}`;
            
            // Create player object with position data
            const player = {
              player_id: playerId,
              first_name: playerDetails.first_name,
              last_name: playerDetails.last_name,
              team_color: teamColor,
              position: position.position,
              goals: 0,
              own_goals: 0
            };
            
            console.log(`Created player object:`, player);
            
            // Initialize goals tracking
            initialGoals[playerId] = 0;
            initialOwnGoals[playerId] = 0;
            
            // Add to appropriate team based on home/away logic for academy matches
            const isAcademyMatch = match.team_type === 'academy' || match.match_type === 'Friendly';
            const isHome = match.is_home === '1' || match.is_home === true;
            
            // Debug logging
            console.log(`Player ${playerDetails.first_name} ${playerDetails.last_name}: team_color=${teamColor}, isAcademyMatch=${isAcademyMatch}, isHome=${isHome}`);
            
            if (isAcademyMatch) {
              if (isHome) {
                // Home team: Current team players (white) go to White team (left side)
                if (teamColor.toLowerCase() === 'white') {
                  whiteTeam.push(player);
                } else if (teamColor.toLowerCase() === 'colour') {
                  colourTeam.push(player);
                }
              } else {
                // Away team: Current team players (white) go to Colour team (right side)
                if (teamColor.toLowerCase() === 'white') {
                  colourTeam.push(player);
                } else if (teamColor.toLowerCase() === 'colour') {
                  whiteTeam.push(player);
                }
              }
            } else {
              // Regular matches: Normal assignment
              if (teamColor.toLowerCase() === 'white') {
                whiteTeam.push(player);
              } else if (teamColor.toLowerCase() === 'colour') {
                colourTeam.push(player);
              }
            }
          }
          
          console.log("Final white team:", whiteTeam);
          console.log("Final colour team:", colourTeam);
          
          // Set teams data
          setTeams({
            White: whiteTeam,
            Colour: colourTeam
          });
          
          // Pre-populate goals with initial values
          setGoals(initialGoals);
          setOwnGoals(initialOwnGoals);
        } else {
          console.log("No player positions found, falling back to get_teams.php");
          // Fallback to the original get_teams.php if no positions are found
          const teamsResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_teams.php?match_id=${match.match_id}`);
          if (teamsResponse.data.success) {
            console.log("Teams data from get_teams.php:", teamsResponse.data.teams);
            
            // Get teams data with any existing goal counts
            const teamsData = teamsResponse.data.teams;
            
            // Initialize goals object from teams data
            const initialGoalsFromTeams = {};
            const initialOwnGoalsFromTeams = {};
            Object.entries(teamsData).forEach(([teamName, players]) => {
              players.forEach(player => {
                if (player.player_id) {
                  // If goals field exists in the player data, use it
                  if (player.goals !== undefined) {
                    initialGoalsFromTeams[player.player_id] = parseInt(player.goals, 10) || 0;
                  }
                  if (player.own_goals !== undefined) {
                    initialOwnGoalsFromTeams[player.player_id] = parseInt(player.own_goals, 10) || 0;
                  }
                }
              });
            });
            
            // Set teams data
            setTeams(teamsData);
            
            // Pre-populate goals with any values we got from teams data
            setGoals(initialGoalsFromTeams);
            setOwnGoals(initialOwnGoalsFromTeams);
          }
          }
        }

        // Fetch existing goals data
        const goalsResponse = await api.get(`${process.env.REACT_APP_API_URL}/get_match_result.php?match_id=${match.match_id}`);
        if (goalsResponse.data.success) {
          const matchData = goalsResponse.data;
          // Process both team data
          const scorersData = matchData.scorers || {};
          const ownGoalsData = matchData.own_goals || {};
          
          // Get players from both teams who have scored
          const allScorers = [
            ...(scorersData[teamA] || scorersData['White'] || []),
            ...(scorersData[teamB] || scorersData['Colour'] || [])
          ];
          
          const allOwnGoals = [
            ...(ownGoalsData[teamA] || ownGoalsData['White'] || []),
            ...(ownGoalsData[teamB] || ownGoalsData['Colour'] || [])
          ];
          
          // Update goals for players who have scored
          setGoals(prevGoals => {
            const updatedGoals = { ...prevGoals };
            
            allScorers.forEach(player => {
              if (player.player_id) {
                updatedGoals[player.player_id] = parseInt(player.goals, 10) || 0;
              }
            });
            
            return updatedGoals;
          });

          // Update own goals
          setOwnGoals(prevOwnGoals => {
            const updatedOwnGoals = { ...prevOwnGoals };
            
            allOwnGoals.forEach(player => {
              if (player.player_id) {
                updatedOwnGoals[player.player_id] = parseInt(player.own_goals, 10) || 0;
              }
            });
            
            return updatedOwnGoals;
          });

          const storedOppositionOwnGoals = matchData.match?.opposition_own_goals
            ?? matchData.data?.opposition_own_goals
            ?? matchData.match_details?.opposition_own_goals;
          if (storedOppositionOwnGoals !== undefined && storedOppositionOwnGoals !== null) {
            setOppositionOwnGoals(parseInt(storedOppositionOwnGoals, 10) || 0);
          }
        }

        // Always fetch attendance data regardless of teams status
        await fetchAttendance();
      } catch (error) {
        console.error('Error fetching match data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAttendance = async () => {
      try {
        const teamId = sessionStorage.getItem('selected_team_id');
        
        const response = await api.get(`${process.env.REACT_APP_API_URL}/get_attendance.php?match_id=${match.match_id}&team_id=${teamId}`);
        const data = response.data;
        
        if (data.success) {
          setAttendance(data.data);
        }
      } catch (error) {
        console.error('Error fetching attendance:', error);
      }
    };

    fetchAttendance().then(() => fetchData());
  }, [match.match_id, teamA, teamB]);

  const calculateTeamScores = () => {
    const teamScores = {
      colour: 0,
      white: 0
    };

    // Process all players from both teams regardless of which object key they're under
    const allPlayers = [
      ...(teams.White || []),
      ...(teams.Colour || [])
    ];

    allPlayers.forEach(player => {
      const playerGoals = parseInt(goals[player.player_id]) || 0;
      const playerOwnGoals = parseInt(ownGoals[player.player_id]) || 0;
      
      // Use the player's individual team_color property
      if (player.team_color.toLowerCase() === 'white') {
        teamScores.white += playerGoals;
        // Own goals scored by white team count for colour team
        teamScores.colour += playerOwnGoals;
      } else {
        teamScores.colour += playerGoals;
        // Own goals scored by colour team count for white team
        teamScores.white += playerOwnGoals;
      }
    });

    return teamScores;
  };

  const getOppositionOwnGoalCount = () => parseInt(oppositionOwnGoals, 10) || 0;

  const getOurScore = () => {
    if (!isAcademyOrSquad) {
      return calculateTeamScores().white;
    }
    const allPlayers = [
      ...(teams.White || []),
      ...(teams.Colour || [])
    ];
    const playerGoals = allPlayers.reduce(
      (sum, player) => sum + (parseInt(goals[player.player_id], 10) || 0),
      0
    );
    return playerGoals + getOppositionOwnGoalCount();
  };

  const handleGoalChange = (playerId, value) => {
    // Allow empty string, otherwise convert to number
    const newValue = value === '' ? '' : parseInt(value, 10);
    setGoals(prev => ({
      ...prev,
      [playerId]: newValue
    }));
  };

  const handleOwnGoalChange = (playerId, value) => {
    // Allow empty string, otherwise convert to number
    const newValue = value === '' ? '' : parseInt(value, 10);
    setOwnGoals(prev => ({
      ...prev,
      [playerId]: newValue
    }));
  };

  const handleOppositionOwnGoalChange = (value) => {
    setOppositionOwnGoals(value === '' ? '' : parseInt(value, 10) || 0);
  };

  const handleSubmit = async () => {
    try {
      // Validate opposition score for academy teams
      if (isAcademyOrSquad && (oppositionScore === '' || oppositionScore === null || oppositionScore === undefined)) {
        showError('Please enter the opposition score before submitting.');
        return;
      }

      const teamScores = calculateTeamScores();
      const rawTeamId = match.team_id || sessionStorage.getItem('selected_team_id');
      const teamId = parseInt(rawTeamId, 10);
      const matchId = parseInt(match.match_id, 10);
      
      if (!teamId) {
        showError('No team ID found. Please try refreshing the page.');
        return;
      }

      // Format goals object to ensure all values are numbers
      const formattedGoals = {};
      Object.entries(goals).forEach(([playerId, goals]) => {
        formattedGoals[playerId] = parseInt(goals, 10) || 0;
      });

      // Format own goals object to ensure all values are numbers
      const formattedOwnGoals = {};
      Object.entries(ownGoals).forEach(([playerId, ownGoals]) => {
        formattedOwnGoals[playerId] = parseInt(ownGoals, 10) || 0;
      });

      const payload = {
        match_id: matchId,
        team_id: teamId,
        score_colour: isAcademyOrSquad ? (parseInt(oppositionScore, 10) || 0) : parseInt(teamScores.colour, 10),
        score_white: isAcademyOrSquad ? getOurScore() : parseInt(teamScores.white, 10),
        goals: formattedGoals,
        own_goals: formattedOwnGoals,
        opposition_own_goals: isAcademyOrSquad ? getOppositionOwnGoalCount() : 0
      };

      console.log('Submitting match result with data:', payload);

      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/update_match_result.php`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        showSuccess('Match result updated successfully');
        
        // If the API returned updated match data, use it to refresh the parent component
        if (response.data.updated_match && response.data.updated_scorers) {
          // Pass the updated data to the parent component
          onUpdate?.({
            updatedMatch: response.data.updated_match,
            updatedScorers: response.data.updated_scorers
          });
        } else {
          // Fallback to the original onUpdate call
          onUpdate?.();
        }
        
        onClose();
      } else {
        console.error('Server response:', response.data);
        showError(response.data.message || 'Failed to update match result');
      }
    } catch (error) {
      console.error('Error updating match result:', error);
      showError('An error occurred while updating the match result');
    }
  };

  const publishTeams = async () => {
    try {
      setIsPublishing(true);
      
      // Get players who responded Yes
      const yesPlayers = attendance.yes || [];
      
      if (yesPlayers.length === 0) {
        showError('No players found who responded Yes to this match');
        setIsPublishing(false);
        return;
      }
      
      // For academy matches, all current team players should be on the same team
      const isAcademyMatch = match.team_type === 'academy' || match.match_type === 'Friendly';
      const isHome = match.is_home === '1' || match.is_home === true;
      
      let whitePlayers, colourPlayers;
      
      if (isAcademyMatch) {
        if (isHome) {
          // Home match: All current team players go to White team (left side)
          whitePlayers = yesPlayers;
          colourPlayers = [];
        } else {
          // Away match: All current team players go to Colour team (right side)
          whitePlayers = [];
          colourPlayers = yesPlayers;
        }
      } else {
        // Regular matches: Randomly split players into White and Colour teams
        const shuffled = [...yesPlayers].sort(() => 0.5 - Math.random());
        const halfLength = Math.ceil(shuffled.length / 2);
        whitePlayers = shuffled.slice(0, halfLength);
        colourPlayers = shuffled.slice(halfLength);
      }
      
      // Prepare the payload for the API
      const payload = {
        match_id: match.match_id,
        white_players: whitePlayers.map(player => ({ player_id: player.player_id })),
        colour_players: colourPlayers.map(player => ({ player_id: player.player_id })),
        skip_sms: isMatchFinished() // Skip SMS notifications if match is finished
      };
      
      // Send the request to publish teams
      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/publish_teams.php`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        // Instead of reloading the page, update the teams state directly
        const formattedWhiteTeam = whitePlayers.map(player => ({
          player_id: player.player_id,
          first_name: player.first_name,
          last_name: player.last_name,
          team_color: 'white',
          goals: 0,
          own_goals: 0
        }));
        
        const formattedColourTeam = colourPlayers.map(player => ({
          player_id: player.player_id,
          first_name: player.first_name,
          last_name: player.last_name,
          team_color: 'colour',
          goals: 0,
          own_goals: 0
        }));
        
        // Update the teams state with the new teams
        setTeams({
          White: formattedWhiteTeam,
          Colour: formattedColourTeam
        });
        
        // Initialize goals and own goals for new teams
        const initialGoals = {};
        const initialOwnGoals = {};
        
        [...formattedWhiteTeam, ...formattedColourTeam].forEach(player => {
          initialGoals[player.player_id] = 0;
          initialOwnGoals[player.player_id] = 0;
        });
        
        setGoals(initialGoals);
        setOwnGoals(initialOwnGoals);
        
        // Show success notification
        showSuccess(isMatchFinished() 
          ? 'Teams published successfully (without SMS notifications)' 
          : 'Teams published successfully'
        );
      } else {
        console.error('Server response:', response.data);
        showError(response.data.message || 'Failed to publish teams');
      }
    } catch (error) {
      console.error('Error publishing teams:', error);
      showError('An error occurred while publishing teams');
    } finally {
      setIsPublishing(false);
    }
  };

  const renderPlayersTable = () => {
    if (isLoading) {
      return <div className="loading">Loading match data...</div>;
    }

    // Collect all players from both teams
    const allPlayers = [
      ...(teams.White || []),
      ...(teams.Colour || [])
    ];

    // Check if we have any players
    if (allPlayers.length === 0) {
      return (
        <div className="no-teams-message">
          <p>No player teams have been published yet.</p>
          <p>You need to publish teams first to assign goals to players.</p>
          <button 
            className="publish-teams-button"
            onClick={publishTeams}
            disabled={isPublishing}
          >
            {isPublishing ? 'Publishing...' : 'Publish Teams Now'}
          </button>
          <p className="info-text">
            {isMatchFinished() 
              ? 'The match has already finished. Teams will be published without sending SMS notifications.' 
              : `This will randomly assign players to ${teamA} and ${teamB} teams.`}
          </p>
        </div>
      );
    }

    // Group players by their actual team_color value (not object keys)
    const sortPlayersByName = (players) =>
      [...players].sort((a, b) => {
        const last = (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' });
        if (last !== 0) {
          return last;
        }
        return (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' });
      });

    const whitePlayers = sortPlayersByName(allPlayers.filter(player =>
      String(player.team_color).toLowerCase() === 'white'
    ));
    
    const colourPlayers = sortPlayersByName(allPlayers.filter(player =>
      String(player.team_color).toLowerCase() === 'colour'
    ));

    // Log for debugging
    console.log("Sorted players:", {
      whitePlayers,
      colourPlayers,
      teamA,
      teamB
    });

    return (
      <table className="players-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Goals</th>
            <th>Own Goals</th>
          </tr>
        </thead>
        <tbody>
          {/* Team A (White) */}
          {whitePlayers.length > 0 && (
            <React.Fragment>
              {isFriendlyMode && (
                <tr className="team-header">
                  <th colSpan="3">{teamA} Team</th>
                </tr>
              )}
              {whitePlayers.map(player => (
                <tr key={player.player_id}>
                  <td>
                    <div className="player-info">
                      <span className="player-name">{`${player.first_name} ${player.last_name}`}</span>
                      {isFriendlyMode && (
                        <span className="player-team">{teamA}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={goals[player.player_id] ?? ''}
                      onChange={(e) => handleGoalChange(player.player_id, e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={ownGoals[player.player_id] ?? ''}
                      onChange={(e) => handleOwnGoalChange(player.player_id, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </React.Fragment>
          )}
          
          {/* Team B (Colour) */}
          {colourPlayers.length > 0 && isFriendlyMode && (
            <React.Fragment>
              <tr className="team-header">
                <th colSpan="3">{teamB} Team</th>
              </tr>
              {colourPlayers.map(player => (
                <tr key={player.player_id}>
                  <td>
                    <div className="player-info">
                      <span className="player-name">{`${player.first_name} ${player.last_name}`}</span>
                      <span className="player-team">{teamB}</span>
                    </div>
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={goals[player.player_id] ?? ''}
                      onChange={(e) => handleGoalChange(player.player_id, e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      min="0"
                      value={ownGoals[player.player_id] ?? ''}
                      onChange={(e) => handleOwnGoalChange(player.player_id, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </React.Fragment>
          )}
          {isAcademyOrSquad && (
            <tr className="opposition-own-goal-row">
              <td>
                <div className="player-info">
                  <span className="player-name">Own Goal</span>
                  <span className="opposition-own-goal-hint">
                    This is own goals scored by {match.opposition || 'the opposition'}
                  </span>
                </div>
              </td>
              <td></td>
              <td>
                <input
                  type="number"
                  min="0"
                  value={oppositionOwnGoals}
                  onChange={(e) => handleOppositionOwnGoalChange(e.target.value)}
                  onFocus={(e) => e.target.select()}
                />
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  const teamScores = calculateTeamScores();

  return (
    <div className="modal-overlay">
      <div className="modal-content scores-modal form-group scrollable">
        <button className="modal-close" onClick={onClose}>×</button>
        <h2>{isAcademyOrSquad ? 'PLAYER STATS' : 'MATCH RESULT'}</h2>
        {isFriendlyMode && (
          <div className="score-inputs">
            <div>
              <label>Score ({teamA})</label>
              <input 
                type="number" 
                value={teamA.toLowerCase().includes('white') ? teamScores.white : 
                       teamA.toLowerCase().includes('colour') || teamA.toLowerCase().includes('color') ? teamScores.colour : 
                       match.is_home ? teamScores.white : teamScores.colour}
                disabled
              />
            </div>
            <div>
              <label>Score ({teamB})</label>
              <input 
                type="number" 
                value={teamB.toLowerCase().includes('white') ? teamScores.white : 
                       teamB.toLowerCase().includes('colour') || teamB.toLowerCase().includes('color') ? teamScores.colour : 
                       match.is_home ? teamScores.colour : teamScores.white}
                disabled
              />
            </div>
          </div>
        )}
        {isAcademyOrSquad && (
          <div className="score-inputs">
            <div>
              <label>Our Score</label>
              <input 
                type="number" 
                value={getOurScore()}
                disabled
              />
            </div>
            <div>
              <label>{match.opposition || 'Opposition'}</label>
              <input 
                type="number" 
                min="0"
                value={oppositionScore}
                onChange={(e) => {
                  const value = e.target.value;
                  setOppositionScore(value === '' ? '' : parseInt(value, 10) || 0);
                }}
                onFocus={(e) => e.target.select()}
                placeholder="0"
              />
            </div>
          </div>
        )}
        <div className="goals-section">
          {renderPlayersTable()}
        </div>
        <div className="modal-actions">
          <button className="submit-button full-width" onClick={handleSubmit}>
            {isAcademyOrSquad ? 'Update Player Stats' : 'Update Score'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScoresModal; 